export const RELATION_VALIDATION_MESSAGES = {
  PARENT_AS_SPOUSE_OR_CHILD: "You cannot assign your parent as a spouse or child.",
  CHILD_AS_PARENT_OR_SPOUSE: "You cannot assign your child as a parent or spouse.",
  SAME_PERSON_PARENT_AND_SPOUSE:
    "Invalid relationship: same person cannot be both parent and spouse.",
  UNDER_18_SPOUSE: "You cannot assign a spouse to a person under 18.",
  CIRCULAR_RELATION: "Circular relationship detected. Please check family hierarchy.",
};

export const normalizeRelationId = (value) => {
  if (value === undefined || value === null) return "";
  const normalized = String(value).trim();
  if (!normalized) return "";
  const lowered = normalized.toLowerCase();
  if (lowered === "null" || lowered === "undefined") return "";
  return normalized;
};

const toNumber = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getMemberId = (member) =>
  normalizeRelationId(member?.id ?? member?.record_id ?? member?.memberId);

export const getMemberParentId = (member) =>
  normalizeRelationId(
    member?.parentId ??
      member?.parent_id ??
      member?.parentid_c ??
      member?.parentID ??
      member?.parent_record_id
  );

export const getMemberSpouseId = (member) =>
  normalizeRelationId(
    member?.spouseId ?? member?.spouse_id ?? member?.spouseid_c ?? member?.spouseID
  );

export const getMemberAge = (member) => toNumber(member?.age ?? member?.age_c);

const buildChildrenMap = (members = []) => {
  const childrenMap = new Map();

  members.forEach((member) => {
    const memberId = getMemberId(member);
    const parentId = getMemberParentId(member);

    if (!memberId || !parentId) return;

    if (!childrenMap.has(parentId)) {
      childrenMap.set(parentId, new Set());
    }
    childrenMap.get(parentId).add(memberId);
  });

  return childrenMap;
};

export const getAncestorIds = (memberId, members = []) => {
  const normalizedMemberId = normalizeRelationId(memberId);
  if (!normalizedMemberId) return new Set();

  const byId = new Map();
  members.forEach((member) => {
    const id = getMemberId(member);
    if (id) byId.set(id, member);
  });

  const ancestors = new Set();
  let currentId = normalizedMemberId;

  while (currentId) {
    const currentMember = byId.get(currentId);
    const parentId = getMemberParentId(currentMember);
    if (!parentId || ancestors.has(parentId)) break;
    ancestors.add(parentId);
    currentId = parentId;
  }

  return ancestors;
};

export const getDescendantIds = (memberId, members = []) => {
  const normalizedMemberId = normalizeRelationId(memberId);
  if (!normalizedMemberId) return new Set();

  const childrenMap = buildChildrenMap(members);
  const descendants = new Set();
  const queue = [normalizedMemberId];

  while (queue.length) {
    const currentId = queue.shift();
    const children = childrenMap.get(currentId);
    if (!children) continue;

    children.forEach((childId) => {
      if (descendants.has(childId)) return;
      descendants.add(childId);
      queue.push(childId);
    });
  }

  return descendants;
};

export const isInAncestorOrDescendantChain = ({ members = [], memberId, targetId }) => {
  const normalizedMemberId = normalizeRelationId(memberId);
  const normalizedTargetId = normalizeRelationId(targetId);

  if (!normalizedMemberId || !normalizedTargetId) {
    return { inChain: false, isAncestor: false, isDescendant: false };
  }

  const ancestorIds = getAncestorIds(normalizedMemberId, members);
  const descendantIds = getDescendantIds(normalizedMemberId, members);

  const isAncestor = ancestorIds.has(normalizedTargetId);
  const isDescendant = descendantIds.has(normalizedTargetId);

  return {
    inChain: isAncestor || isDescendant,
    isAncestor,
    isDescendant,
  };
};

export const validateAgeForSpouse = ({ relationType, age }) => {
  if (relationType !== "Spouse") return { valid: true, message: "" };

  const numericAge = toNumber(age);
  if (numericAge === null) return { valid: true, message: "" };

  if (numericAge < 18) {
    return {
      valid: false,
      message: RELATION_VALIDATION_MESSAGES.UNDER_18_SPOUSE,
    };
  }

  return { valid: true, message: "" };
};

export const detectCircularRelation = ({ relationType, members = [], subjectId, targetId }) => {
  const normalizedSubjectId = normalizeRelationId(subjectId);
  const normalizedTargetId = normalizeRelationId(targetId);

  if (!normalizedSubjectId || !normalizedTargetId) {
    return { hasCycle: false, message: "" };
  }

  if (normalizedSubjectId === normalizedTargetId) {
    return {
      hasCycle: true,
      message: RELATION_VALIDATION_MESSAGES.CIRCULAR_RELATION,
    };
  }

  if (relationType === "Parent") {
    const subjectAncestors = getAncestorIds(normalizedSubjectId, members);
    if (subjectAncestors.has(normalizedTargetId)) {
      return {
        hasCycle: true,
        message: RELATION_VALIDATION_MESSAGES.CIRCULAR_RELATION,
      };
    }
  }

  if (relationType === "Child") {
    const subjectDescendants = getDescendantIds(normalizedSubjectId, members);
    if (subjectDescendants.has(normalizedTargetId)) {
      return {
        hasCycle: true,
        message: RELATION_VALIDATION_MESSAGES.CIRCULAR_RELATION,
      };
    }
  }

  return { hasCycle: false, message: "" };
};

export const validateRelation = ({
  relationType,
  members = [],
  subjectId,
  targetId,
  subjectParentId,
  subjectSpouseId,
}) => {
  const normalizedTargetId = normalizeRelationId(targetId);
  const normalizedParentId = normalizeRelationId(subjectParentId);
  const normalizedSpouseId = normalizeRelationId(subjectSpouseId);

  if (!relationType || !normalizedTargetId) {
    return { valid: true, message: "" };
  }

  if (
    (relationType === "Parent" || relationType === "Child") &&
    normalizedSpouseId &&
    normalizedSpouseId === normalizedTargetId
  ) {
    return {
      valid: false,
      message: RELATION_VALIDATION_MESSAGES.SAME_PERSON_PARENT_AND_SPOUSE,
    };
  }

  if (
    (relationType === "Spouse" || relationType === "Child") &&
    normalizedParentId &&
    normalizedParentId === normalizedTargetId
  ) {
    return {
      valid: false,
      message: RELATION_VALIDATION_MESSAGES.PARENT_AS_SPOUSE_OR_CHILD,
    };
  }

  const subjectDescendants = getDescendantIds(subjectId, members);
  if (
    (relationType === "Parent" || relationType === "Spouse") &&
    subjectDescendants.has(normalizedTargetId)
  ) {
    return {
      valid: false,
      message: RELATION_VALIDATION_MESSAGES.CHILD_AS_PARENT_OR_SPOUSE,
    };
  }

  const chainStatus = isInAncestorOrDescendantChain({
    members,
    memberId: subjectId,
    targetId: normalizedTargetId,
  });

  if (relationType === "Spouse" && chainStatus.isAncestor) {
    return {
      valid: false,
      message: RELATION_VALIDATION_MESSAGES.PARENT_AS_SPOUSE_OR_CHILD,
    };
  }

  if (relationType === "Spouse" && chainStatus.isDescendant) {
    return {
      valid: false,
      message: RELATION_VALIDATION_MESSAGES.CHILD_AS_PARENT_OR_SPOUSE,
    };
  }

  const circularStatus = detectCircularRelation({
    relationType,
    members,
    subjectId,
    targetId: normalizedTargetId,
  });

  if (circularStatus.hasCycle) {
    return {
      valid: false,
      message: circularStatus.message,
    };
  }

  return { valid: true, message: "" };
};
